"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isWeekend = isWeekend;
exports.addBusinessDays = addBusinessDays;
exports.businessDaysBetween = businessDaysBetween;
exports.clipAndMergeIntervals = clipAndMergeIntervals;
exports.netBusinessDays = netBusinessDays;
const SUNDAY = 0;
const SATURDAY = 6;
const formattersByTimezone = new Map();
function formatterFor(timezone) {
    const cached = formattersByTimezone.get(timezone);
    if (cached !== undefined) {
        return cached;
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    formattersByTimezone.set(timezone, formatter);
    return formatter;
}
function toLocal(ms, timezone) {
    const parts = new Map();
    for (const part of formatterFor(timezone).formatToParts(new Date(ms))) {
        if (part.type !== 'literal') {
            parts.set(part.type, Number(part.value));
        }
    }
    return {
        year: parts.get('year') ?? 0,
        month: parts.get('month') ?? 1,
        day: parts.get('day') ?? 1,
        hour: parts.get('hour') ?? 0,
        minute: parts.get('minute') ?? 0,
        second: parts.get('second') ?? 0,
        millisecond: ((ms % 1000) + 1000) % 1000,
    };
}
function fromLocal(local, timezone) {
    const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second, local.millisecond);
    const firstGuess = asUtc - offsetMs(asUtc, timezone);
    const secondOffset = offsetMs(firstGuess, timezone);
    return asUtc - secondOffset;
}
function offsetMs(ms, timezone) {
    const local = toLocal(ms, timezone);
    const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    return asUtc - (ms - local.millisecond);
}
function weekdayOf(local) {
    return new Date(Date.UTC(local.year, local.month - 1, local.day)).getUTCDay();
}
function isWeekendDate(local) {
    const weekday = weekdayOf(local);
    return weekday === SUNDAY || weekday === SATURDAY;
}
function plusDays(local, days) {
    const stepped = new Date(Date.UTC(local.year, local.month - 1, local.day + days));
    return {
        ...local,
        year: stepped.getUTCFullYear(),
        month: stepped.getUTCMonth() + 1,
        day: stepped.getUTCDate(),
    };
}
function startOfDay(local, timezone) {
    return fromLocal({ ...local, hour: 0, minute: 0, second: 0, millisecond: 0 }, timezone);
}
function isWeekend(date, timezone) {
    return isWeekendDate(toLocal(date.getTime(), timezone));
}
function addBusinessDays(startTimestamp, businessDays, timezone) {
    if (businessDays <= 0) {
        return startTimestamp;
    }
    let current = toLocal(startTimestamp, timezone);
    let remaining = businessDays;
    while (remaining > 0) {
        current = plusDays(current, 1);
        if (!isWeekendDate(current)) {
            remaining -= 1;
        }
    }
    return fromLocal(current, timezone);
}
function businessDaysBetween(startMs, endMs, timezone) {
    if (endMs <= startMs) {
        return 0;
    }
    let total = 0;
    let day = toLocal(startMs, timezone);
    let dayStart = startOfDay(day, timezone);
    while (dayStart < endMs) {
        const nextDay = plusDays(day, 1);
        const dayEnd = startOfDay(nextDay, timezone);
        if (!isWeekendDate(day)) {
            const overlap = Math.min(endMs, dayEnd) - Math.max(startMs, dayStart);
            total += overlap / (dayEnd - dayStart);
        }
        day = nextDay;
        dayStart = dayEnd;
    }
    return total;
}
function clipAndMergeIntervals(intervals, startMs, endMs) {
    const clipped = intervals
        .map((interval) => ({
        startMs: Math.max(interval.startMs, startMs),
        endMs: Math.min(interval.endMs ?? endMs, endMs),
    }))
        .filter((interval) => interval.endMs > interval.startMs)
        .sort((left, right) => left.startMs - right.startMs);
    const merged = [];
    for (const interval of clipped) {
        const last = merged[merged.length - 1];
        if (last !== undefined && interval.startMs <= last.endMs) {
            last.endMs = Math.max(last.endMs, interval.endMs);
        }
        else {
            merged.push({ ...interval });
        }
    }
    return merged;
}
function netBusinessDays(startMs, endMs, pausedIntervals, timezone) {
    const gross = businessDaysBetween(startMs, endMs, timezone);
    const paused = clipAndMergeIntervals(pausedIntervals, startMs, endMs).reduce((sum, interval) => sum + businessDaysBetween(interval.startMs, interval.endMs, timezone), 0);
    return Math.max(0, gross - paused);
}
//# sourceMappingURL=business-days.js.map